<template>
    <b-container class="bv-example-row">
        <b-row>
            <b-col>
                <b-alert show variant="light" class="alert alert-custom alert-white alert-shadow fade show gutter-b">
                    <div class="alert-icon">
                        <span class="svg-icon svg-icon-lg">
                            <inline-svg src="media/svg/icons/Home/Door-open.svg" />
                        </span>
                    </div>
                    <div class="alert-text">
                        <strong>Door / Window Sensor</strong> — This is a low-power Bluetooth sensor that
                        reports whenever it is opened or closed. It has no direct internet connection; readings
                        arrive through a nearby sfir bridge, so updates depend on the bridge being online.
                    </div>
                </b-alert>
            </b-col>
        </b-row>

        <!-- Current state + timing -->
        <b-row class="gutter-b" v-if="dataLoaded && current_device">
            <b-col md="6">
                <b-card class="card-custom card-stretch" no-body>
                    <b-card-body class="d-flex flex-column align-items-center justify-content-center py-5">
                        <div class="rounded-circle d-flex align-items-center justify-content-center mb-3"
                            :style="{ width: '90px', height: '90px', backgroundColor: currentState.color }">
                            <span class="font-weight-bolder text-white" style="font-size: 2.2rem;">
                                {{ currentState.glyph }}
                            </span>
                        </div>
                        <h4 class="font-weight-bold text-dark mb-1">{{ currentState.label }}</h4>
                        <span class="text-muted font-size-sm" v-if="lastReadingTime">
                            Last event: {{ lastReadingTime }}
                        </span>
                        <span class="text-muted font-size-sm" v-else>No events yet</span>
                    </b-card-body>
                </b-card>
            </b-col>
            <b-col md="6">
                <b-card class="card-custom card-stretch" header-tag="header" no-body>
                    <template v-slot:header>
                        <div class="d-flex align-items-center w-100">
                            <h6 class="mb-0">Sensor Timing</h6>
                        </div>
                    </template>
                    <b-card-body class="py-3">
                        <div class="d-flex align-items-center mb-3">
                            <b-icon icon="clock-history" variant="primary" font-scale="1.3" class="mr-3"></b-icon>
                            <div>
                                <span class="font-weight-bold d-block">Last Event</span>
                                <span class="text-muted font-size-sm">{{ lastReadingFull || 'No data yet' }}</span>
                            </div>
                        </div>
                        <div class="d-flex align-items-center mb-3">
                            <b-icon icon="arrow-repeat" variant="info" font-scale="1.3" class="mr-3"></b-icon>
                            <div>
                                <span class="font-weight-bold d-block">Avg. Time Between Events</span>
                                <span class="text-muted font-size-sm">{{ avgFrequency }}</span>
                            </div>
                        </div>
                        <div class="d-flex align-items-center">
                            <b-icon icon="graph-up" variant="success" font-scale="1.3" class="mr-3"></b-icon>
                            <div>
                                <span class="font-weight-bold d-block">Total Events</span>
                                <span class="text-muted font-size-sm">{{ activeDataset.length }} events recorded</span>
                            </div>
                        </div>
                    </b-card-body>
                </b-card>
            </b-col>
        </b-row>

        <b-row>
            <b-col>
                <SuitchBatteryWidget :device="current_device" :limitBatteryDate="limitBatteryDate"/>
                <br>
                <SuitchDataDetailsWidget :device="current_device" :dataset="markers" :dataLoaded="dataLoaded"/>
            </b-col>
            <b-col>
                <SuitchTrackerStatusWidget :device="current_device" findMyType="soil" :dataLoaded="dataLoaded"/>
                <br />
                <SuitchDetailsWidget :device="current_device" />
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import {
    FETCH_SINGLE_DEVICE,
    GET_DOOR_EVENTS,
} from "@/core/services/store/devices.module";
import SuitchTrackerStatusWidget from "@/view/content/widgets/suitch/suitch-tracker-status-widget.vue";
import SuitchBatteryWidget from "@/view/content/widgets/suitch/suitch-battery-widget.vue";
import SuitchDataDetailsWidget from "@/view/content/widgets/suitch/suitch-raw-details-widget.vue";
import SuitchDetailsWidget from "@/view/content/widgets/suitch/suitch-details-widget.vue";

export default {
    data() {
        return {
            limitBatteryDate: "",
            current_device: null,
            markers: [],
            activeDataset: [],
            dataLoaded: null,
        };
    },

    computed: {
        // Maps the raw stored value (the door sensor emits "O"/"C" over NUS) to a
        // human-readable state. Tolerant of full words in case the payload changes.
        currentState() {
            if (this.activeDataset.length === 0) {
                return { label: "No Data", glyph: "—", color: "#B0BEC5" };
            }
            return this.stateInfo(this.activeDataset[0].value);
        },
        lastReadingTime() {
            if (this.activeDataset.length === 0) return null;
            return this.getRelativeTime(this.activeDataset[0].created_at);
        },
        lastReadingFull() {
            if (this.activeDataset.length === 0) return null;
            return this.formatDate(this.activeDataset[0].created_at);
        },
        avgFrequency() {
            if (this.activeDataset.length < 2) return 'Not enough data';
            const first = new Date(this.activeDataset[0].created_at);
            const lastM = new Date(this.activeDataset[this.activeDataset.length - 1].created_at);
            const totalMs = Math.abs(lastM - first);
            const avgMs = totalMs / (this.activeDataset.length - 1);
            const avgMin = Math.round(avgMs / 60000);
            if (avgMin < 60) return '~' + avgMin + ' minutes between events';
            const avgHours = Math.round(avgMin / 60);
            if (avgHours < 24) return '~' + avgHours + ' hours between events';
            const avgDays = Math.round(avgHours / 24);
            return '~' + avgDays + ' days between events';
        },
    },

    components: {
        SuitchTrackerStatusWidget,
        SuitchBatteryWidget,
        SuitchDataDetailsWidget,
        SuitchDetailsWidget,
    },

    methods: {
        stateInfo(rawValue) {
            const v = String(rawValue == null ? "" : rawValue).trim().toUpperCase();
            if (v === "O" || v.indexOf("OPEN") === 0) {
                return { label: "Open", glyph: "⤢", color: "#EF5350" };
            }
            if (v === "C" || v.indexOf("CLOS") === 0) {
                return { label: "Closed", glyph: "▣", color: "#66BB6A" };
            }
            return { label: rawValue != null ? String(rawValue) : "Unknown", glyph: "?", color: "#B0BEC5" };
        },
        getRelativeTime(dateString) {
            if (!dateString) return null;
            const date = new Date(dateString);
            if (isNaN(date)) return 'N/A';
            const now = new Date();
            const seconds = Math.floor((now - date) / 1000);
            if (seconds < 60) return 'Just now';
            const minutes = Math.floor(seconds / 60);
            if (minutes < 60) return minutes + 'm ago';
            const hours = Math.floor(minutes / 60);
            if (hours < 24) return hours + 'h ago';
            const days = Math.floor(hours / 24);
            return days + 'd ago';
        },
        formatDate(dateString) {
            if (!dateString) return null;
            const options = {
                day: '2-digit', month: '2-digit', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            };
            try {
                const date = new Date(dateString);
                if (isNaN(date)) return null;
                return new Intl.DateTimeFormat(undefined, options).format(date);
            } catch (e) {
                return null;
            }
        },
    },

    mounted() {
        const device_token = this.$route.params.token;

        this.$store
            .dispatch(FETCH_SINGLE_DEVICE, { device_token })
            .then((data) => {
                this.current_device = data;
            });

        this.$store
            .dispatch(GET_DOOR_EVENTS, { device_token: device_token })
            .then((data) => {
                if (data && data.length > 0) {
                    const imarkers = data.map((item) => {
                        return {
                            value: item.value,
                            created_at: item.created_at,
                            bridge_token: item.bridge_token || null,
                            bridge_name: item.bridge_name || null,
                            showTooltip: false,
                        };
                    });
                    // Newest first
                    imarkers.sort(function (a, b) {
                        return new Date(b.created_at) - new Date(a.created_at);
                    });
                    this.limitBatteryDate = imarkers[imarkers.length - 1].created_at;
                    this.markers = imarkers;
                    this.activeDataset = imarkers;
                }
                this.dataLoaded = true;
            })
            .catch(() => {
                this.dataLoaded = true;
            });
    },
};
</script>
